import { ApiError } from './api';
import type { AdminApi } from './admin-api';
import {
  getDefaultAdminSessionStore,
  type AdminSession,
  type AdminSessionStore,
} from './admin-session';

export const ADMIN_AUTH_CHANNEL = 'autocontrol-admin-auth';
export const ADMIN_REFRESH_LOCK = 'autocontrol-admin-refresh';

const REFRESH_SESSION_INVALID_CODES = new Set([
  'REFRESH_TOKEN_INVALID',
  'REFRESH_TOKEN_REUSED',
  'AUTH_SESSION_REVOKED',
  'UNAUTHENTICATED',
]);

const LOGOUT_ALREADY_DEAD_CODES = new Set([
  'AUTH_SESSION_REVOKED',
  'REFRESH_TOKEN_INVALID',
  'REFRESH_TOKEN_REUSED',
]);

const BOOTSTRAP_TECHNICAL_ERROR = 'Не удалось проверить сеанс. Проверьте соединение и повторите.';
const LOGOUT_TECHNICAL_ERROR = 'Не удалось завершить выход. Проверьте соединение и повторите.';
const REFRESH_LOCK_UNAVAILABLE_ERROR =
  'Не удалось безопасно обновить сеанс. Используйте поддерживаемый браузер и повторите.';

export type AdminAuthPhase = 'BOOTSTRAPPING' | 'AUTHENTICATED' | 'ANONYMOUS';

export type AdminAuthMessage =
  | { readonly type: 'LOGIN_ESTABLISHED' }
  | { readonly type: 'SESSION_REFRESHED' }
  | { readonly type: 'LOGOUT' }
  | { readonly type: 'SESSION_INVALID' };

export interface AdminAuthSnapshot {
  readonly phase: AdminAuthPhase;
  readonly session: AdminSession | null;
  readonly bootstrapError: string | null;
  readonly logoutError: string | null;
}

export interface AdminAuthBus {
  readonly post: (message: AdminAuthMessage) => void;
  readonly subscribe: (listener: (message: AdminAuthMessage) => void) => () => void;
  readonly dispose: () => void;
}

export class AdminLogoutError extends Error {
  public constructor(message = LOGOUT_TECHNICAL_ERROR) {
    super(message);
    this.name = 'AdminLogoutError';
  }
}

export class AdminRefreshLockError extends Error {
  public constructor(message = REFRESH_LOCK_UNAVAILABLE_ERROR) {
    super(message);
    this.name = 'AdminRefreshLockError';
  }
}

export function createMemoryAuthBus(): AdminAuthBus {
  const listeners = new Set<(message: AdminAuthMessage) => void>();
  return {
    post(message) {
      for (const listener of [...listeners]) {
        listener(message);
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose() {
      listeners.clear();
    },
  };
}

function createBroadcastAuthBus(): AdminAuthBus {
  if (typeof BroadcastChannel === 'undefined') {
    return createMemoryAuthBus();
  }

  const channel = new BroadcastChannel(ADMIN_AUTH_CHANNEL);
  const listeners = new Set<(message: AdminAuthMessage) => void>();
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const data = event.data;
    if (!isAdminAuthMessage(data)) {
      return;
    }
    for (const listener of [...listeners]) {
      listener(data);
    }
  };

  return {
    post(message) {
      channel.postMessage(message);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose() {
      listeners.clear();
      channel.close();
    },
  };
}

function isAdminAuthMessage(value: unknown): value is AdminAuthMessage {
  if (typeof value !== 'object' || value === null || !('type' in value)) {
    return false;
  }
  const type = (value as { type: unknown }).type;
  return (
    type === 'LOGIN_ESTABLISHED' ||
    type === 'SESSION_REFRESHED' ||
    type === 'LOGOUT' ||
    type === 'SESSION_INVALID'
  );
}

function isRefreshSessionInvalid(failure: unknown): boolean {
  return (
    failure instanceof ApiError && REFRESH_SESSION_INVALID_CODES.has(failure.problem.code ?? '')
  );
}

function isExpiredBearer(failure: unknown): boolean {
  return failure instanceof ApiError && failure.problem.code === 'UNAUTHENTICATED';
}

function isLogoutSessionAlreadyDead(failure: unknown): boolean {
  return failure instanceof ApiError && LOGOUT_ALREADY_DEAD_CODES.has(failure.problem.code ?? '');
}

/**
 * Serializes cookie refresh across tabs via Web Locks.
 * Unsupported browsers fail closed before any AUTH-02 network request.
 */
export async function defaultRequestLock<T>(name: string, work: () => Promise<T>): Promise<T> {
  const locks = globalThis.navigator?.locks;
  if (locks === undefined || typeof locks.request !== 'function') {
    throw new AdminRefreshLockError();
  }
  return locks.request(name, work);
}

export interface AdminAuthCoordinator {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => AdminAuthSnapshot;
  readonly bootstrap: () => Promise<void>;
  readonly login: (username: string, password: string) => Promise<AdminSession>;
  readonly logout: () => Promise<void>;
  /**
   * One-shot access-token recovery for ordinary business requests after 401 UNAUTHENTICATED.
   * Under Web Lock: reuse a newer store token, otherwise rotate via cookie refresh.
   * Session-invalid refresh codes converge anonymously; network/5xx rethrow without SESSION_INVALID.
   */
  readonly recoverExpiredAccessToken: (
    failedAccessToken: string | null,
  ) => Promise<AdminSession | null>;
  readonly dispose: () => void;
}

export function createAdminAuthCoordinator(
  api: AdminApi,
  deps: {
    readonly bus?: AdminAuthBus;
    readonly store?: AdminSessionStore;
    readonly requestLock?: <T>(name: string, work: () => Promise<T>) => Promise<T>;
  } = {},
): AdminAuthCoordinator {
  const ownsBus = deps.bus === undefined;
  const bus = deps.bus ?? createBroadcastAuthBus();
  const store = deps.store ?? getDefaultAdminSessionStore();
  const requestLock = deps.requestLock ?? defaultRequestLock;
  const listeners = new Set<() => void>();
  let snapshot: AdminAuthSnapshot = {
    phase: store.read() === null ? 'BOOTSTRAPPING' : 'AUTHENTICATED',
    session: store.read(),
    bootstrapError: null,
    logoutError: null,
  };
  let published: AdminAuthSnapshot = snapshot;
  let disposed = false;
  let bootstrapped = snapshot.phase === 'AUTHENTICATED';
  let sessionInvalidEmitted = false;

  const emit = (): void => {
    for (const listener of listeners) {
      listener();
    }
  };

  const setSnapshot = (next: AdminAuthSnapshot): void => {
    snapshot = next;
    emit();
  };

  const applySession = (session: AdminSession | null, phase: AdminAuthPhase): void => {
    if (session === null) {
      store.clear();
    } else {
      sessionInvalidEmitted = false;
      store.write(session);
    }
    setSnapshot({ phase, session, bootstrapError: null, logoutError: null });
  };

  const markSessionInvalid = (): void => {
    applySession(null, 'ANONYMOUS');
    if (sessionInvalidEmitted) {
      return;
    }
    sessionInvalidEmitted = true;
    bus.post({ type: 'SESSION_INVALID' });
  };

  const recoverFromCookie = async (
    options: { readonly force?: boolean } = {},
  ): Promise<AdminSession | null> => {
    return requestLock(ADMIN_REFRESH_LOCK, async () => {
      if (options.force !== true) {
        const existing = store.read();
        if (existing !== null) {
          return existing;
        }
      }
      try {
        const recovered = await api.refreshFromCookie();
        bus.post({ type: 'SESSION_REFRESHED' });
        return recovered;
      } catch (failure) {
        if (isRefreshSessionInvalid(failure)) {
          markSessionInvalid();
          return null;
        }
        throw failure;
      }
    });
  };

  const recoverExpiredAccessToken = async (
    failedAccessToken: string | null,
  ): Promise<AdminSession | null> => {
    return requestLock(ADMIN_REFRESH_LOCK, async () => {
      const existing = store.read();
      if (existing !== null && existing.accessToken !== failedAccessToken) {
        return existing;
      }
      try {
        const recovered = await api.refreshFromCookie();
        applySession(recovered, 'AUTHENTICATED');
        bus.post({ type: 'SESSION_REFRESHED' });
        return recovered;
      } catch (failure) {
        if (isRefreshSessionInvalid(failure)) {
          markSessionInvalid();
          return null;
        }
        throw failure;
      }
    });
  };

  const unsubscribeSession = store.subscribe(() => {
    emit();
  });

  const unsubscribeBus = bus.subscribe((message) => {
    if (disposed) {
      return;
    }
    if (message.type === 'LOGOUT') {
      applySession(null, 'ANONYMOUS');
      return;
    }
    if (message.type === 'SESSION_INVALID') {
      sessionInvalidEmitted = true;
      applySession(null, 'ANONYMOUS');
      return;
    }
    if (message.type === 'LOGIN_ESTABLISHED' || message.type === 'SESSION_REFRESHED') {
      if (store.read() !== null) {
        return;
      }
      void recoverFromCookie()
        .then((session) => {
          if (session === null) {
            return;
          }
          applySession(session, 'AUTHENTICATED');
        })
        .catch(() => {
          if (store.read() !== null) {
            return;
          }
          setSnapshot({
            phase: 'ANONYMOUS',
            session: null,
            bootstrapError: BOOTSTRAP_TECHNICAL_ERROR,
            logoutError: null,
          });
        });
    }
  });

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      const live = store.read();
      const next: AdminAuthSnapshot =
        live !== null
          ? {
              phase: 'AUTHENTICATED',
              session: live,
              bootstrapError: null,
              logoutError: snapshot.logoutError,
            }
          : snapshot.phase === 'BOOTSTRAPPING'
            ? snapshot
            : {
                phase: 'ANONYMOUS',
                session: null,
                bootstrapError: snapshot.bootstrapError,
                logoutError: snapshot.logoutError,
              };
      if (
        published.phase === next.phase &&
        published.session === next.session &&
        published.bootstrapError === next.bootstrapError &&
        published.logoutError === next.logoutError
      ) {
        return published;
      }
      published = next;
      return published;
    },
    async bootstrap() {
      if (bootstrapped || disposed) {
        return;
      }
      bootstrapped = true;
      if (store.read() !== null) {
        applySession(store.read(), 'AUTHENTICATED');
        return;
      }
      setSnapshot({
        phase: 'BOOTSTRAPPING',
        session: null,
        bootstrapError: null,
        logoutError: null,
      });
      try {
        const session = await recoverFromCookie();
        if (session === null) {
          return;
        }
        applySession(session, 'AUTHENTICATED');
      } catch {
        setSnapshot({
          phase: 'ANONYMOUS',
          session: null,
          bootstrapError: BOOTSTRAP_TECHNICAL_ERROR,
          logoutError: null,
        });
      }
    },
    async login(username, password) {
      const session = await api.login(username, password);
      applySession(session, 'AUTHENTICATED');
      bus.post({ type: 'LOGIN_ESTABLISHED' });
      return session;
    },
    recoverExpiredAccessToken,
    async logout() {
      const current = store.read();
      if (current === null) {
        applySession(null, 'ANONYMOUS');
        bus.post({ type: 'LOGOUT' });
        return;
      }

      const completeLogout = async (): Promise<void> => {
        await api.logout();
        applySession(null, 'ANONYMOUS');
        bus.post({ type: 'LOGOUT' });
      };

      try {
        await completeLogout();
        return;
      } catch (failure) {
        if (isLogoutSessionAlreadyDead(failure)) {
          markSessionInvalid();
          return;
        }
        if (!isExpiredBearer(failure)) {
          setSnapshot({
            phase: 'AUTHENTICATED',
            session: store.read(),
            bootstrapError: null,
            logoutError: LOGOUT_TECHNICAL_ERROR,
          });
          throw new AdminLogoutError();
        }
      }

      let recovered: AdminSession | null;
      try {
        recovered = await recoverFromCookie({ force: true });
      } catch (failure) {
        if (isRefreshSessionInvalid(failure)) {
          markSessionInvalid();
          return;
        }
        setSnapshot({
          phase: 'AUTHENTICATED',
          session: store.read(),
          bootstrapError: null,
          logoutError: LOGOUT_TECHNICAL_ERROR,
        });
        throw new AdminLogoutError();
      }

      if (recovered === null) {
        return;
      }
      applySession(recovered, 'AUTHENTICATED');

      try {
        await completeLogout();
      } catch (failure) {
        if (isLogoutSessionAlreadyDead(failure) || isRefreshSessionInvalid(failure)) {
          markSessionInvalid();
          return;
        }
        setSnapshot({
          phase: 'AUTHENTICATED',
          session: store.read(),
          bootstrapError: null,
          logoutError: LOGOUT_TECHNICAL_ERROR,
        });
        throw new AdminLogoutError();
      }
    },
    dispose() {
      disposed = true;
      unsubscribeBus();
      unsubscribeSession();
      if (ownsBus) {
        bus.dispose();
      }
      listeners.clear();
    },
  };
}

let activeCoordinator: AdminAuthCoordinator | null = null;

export function getAdminAuthCoordinator(
  api: AdminApi,
  deps?: Parameters<typeof createAdminAuthCoordinator>[1],
): AdminAuthCoordinator {
  if (activeCoordinator === null) {
    activeCoordinator = createAdminAuthCoordinator(api, deps);
  }
  return activeCoordinator;
}

export function resetAdminAuthCoordinator(): void {
  activeCoordinator?.dispose();
  activeCoordinator = null;
}
